<script setup>
import { computed, onMounted, onBeforeUnmount, reactive, ref } from "vue";
import { Plus, Search, Pencil, ShieldOff } from "@lucide/vue";
import { api } from "../api";
import { useSessionStore } from "../stores/session";
import PageHeader from "../components/PageHeader.vue";
import TablePager from "../components/TablePager.vue";
import UiDrawer from "../ui/UiDrawer.vue";
import UiSelect from "../ui/UiSelect.vue";
import UiToast from "../ui/UiToast.vue";
import UiButton from "../ui/UiButton.vue";
import UiConfirmDrawer from "../ui/UiConfirmDrawer.vue";
import UiStudentAvatar from "../ui/UiStudentAvatar.vue";
const session = useSessionStore(),
  rows = ref([]),
  students = ref([]),
  search = ref(""),
  page = ref(1),
  modal = ref(false),
  toast = ref(""),
  toastType = ref("success"),
  saving = ref(false),
  deleting = ref(false),
  deleteTarget = ref(null),
  perPage = 15;
const blank = () => ({
    id: null,
    schoolId: session.selectedSchoolId || session.user?.school_id || "",
    studentId: "",
    uid: "",
    label: "",
    status: "active",
  }),
  form = reactive(blank());
const schoolOptions = computed(() =>
  session.schools.map((school) => ({
    value: String(school.id),
    label: school.name,
    description: school.code || "Erva Spor Okulu",
  })),
);
const statusOptions = [
  {
    value: "active",
    label: "Aktif",
    description: "Kart geçişlerde kullanılabilir",
  },
  {
    value: "blocked",
    label: "Blokeli",
    description: "Kart geçişlerde reddedilir",
  },
];
const studentOptions = computed(() => [
  { value: "", label: "Atanmamış", description: "Kartı daha sonra bağla" },
  ...students.value
    .filter((student) => String(student.school_id) === String(form.schoolId))
    .map((student) => ({
      value: String(student.id),
      label: `${student.first_name} ${student.last_name}`,
      description:
        [student.student_no, student.class_name].filter(Boolean).join(" • ") ||
        "Öğrenci",
    })),
]);
const filtered = computed(() =>
    rows.value.filter((r) =>
      `${r.uid} ${r.first_name || ""} ${r.last_name || ""} ${r.label || ""}`
        .toLowerCase()
        .includes(search.value.toLowerCase()),
    ),
  ),
  paged = computed(() =>
    filtered.value.slice((page.value - 1) * perPage, page.value * perPage),
  );
function show(m, t = "success") {
  toast.value = m;
  toastType.value = t;
  setTimeout(() => (toast.value = ""), 3000);
}
async function load() {
  try {
    const q = session.selectedSchoolId
      ? `?schoolId=${session.selectedSchoolId}`
      : "";
    const [a, b] = await Promise.all([
      api(`/api/cards${q}`),
      api(`/api/students${q}`),
    ]);
    rows.value = a.cards || [];
    students.value = b.students || [];
  } catch (e) {
    show(e.message, "error");
  }
}
function open(r = null) {
  Object.assign(form, blank());
  if (r)
    Object.assign(form, {
      id: r.id,
      schoolId: String(r.school_id),
      studentId: r.student_id ? String(r.student_id) : "",
      uid: r.uid,
      label: r.label || "",
      status: r.status,
    });
  modal.value = true;
}
async function save() {
  saving.value = true;
  try {
    await api(form.id ? `/api/cards/${form.id}` : "/api/cards", {
      method: form.id ? "PUT" : "POST",
      body: JSON.stringify(form),
    });
    modal.value = false;
    show("Kart kaydedildi.");
    await load();
  } catch (e) {
    show(e.message, "error");
  } finally {
    saving.value = false;
  }
}
async function block(r) {
  deleteTarget.value = r;
}
async function confirmBlock() {
  if (!deleteTarget.value) return;
  deleting.value = true;
  try {
    await api(`/api/cards/${deleteTarget.value.id}`, { method: "DELETE" });
    show("Kart blokelendi.");
    deleteTarget.value = null;
    await load();
  } catch (e) {
    show(e.message, "error");
  } finally {
    deleting.value = false;
  }
}
const refresh = () => load();
onMounted(() => {
  load();
  window.addEventListener("lopiy:refresh", refresh);
});
onBeforeUnmount(() => window.removeEventListener("lopiy:refresh", refresh));
</script>
<template>
  <PageHeader
    title="Kartlar"
    description="Erva Spor Okulları RFID kartlarını öğrencilere atayın ve durumlarını yönetin."
    ><button v-if="!session.isParent" class="btn-primary" @click="open()">
      <Plus class="size-4" />Kart Ekle
    </button></PageHeader
  >
  <section class="panel overflow-hidden">
    <div class="toolbar">
      <span class="text-sm font-semibold text-slate-500"
        >{{ filtered.length }} kart</span
      ><label class="relative w-full sm:w-72"
        ><Search class="absolute left-3 top-2.5 size-4 text-slate-400" /><input
          v-model="search"
          class="input pl-9"
          placeholder="UID, öğrenci veya etiket ara"
      /></label>
    </div>
    <div class="overflow-x-auto">
      <table class="data-table">
        <thead>
          <tr>
            <th>UID</th>
            <th>Öğrenci</th>
            <th>Okul</th>
            <th>Etiket</th>
            <th>Durum</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in paged" :key="r.id">
            <td class="font-mono font-bold text-ink-900">{{ r.uid }}</td>
            <td>
              <div class="flex min-w-40 items-center gap-3">
                <UiStudentAvatar
                  :src="r.photo_path"
                  :first-name="r.first_name"
                  :last-name="r.last_name"
                  size="sm"
                />
                <span>{{
                  [r.first_name, r.last_name].filter(Boolean).join(" ") ||
                  "Atanmamış"
                }}</span>
              </div>
            </td>
            <td>{{ r.school_name }}</td>
            <td>{{ r.label || "-" }}</td>
            <td>
              <span
                :class="r.status === 'active' ? 'chip-success' : 'chip-danger'"
                >{{ r.status === "active" ? "Aktif" : "Blokeli" }}</span
              >
            </td>
            <td>
              <div class="flex justify-end gap-1">
                <button class="icon-btn size-8" @click="open(r)">
                  <Pencil class="size-3.5" /></button
                ><button
                  class="icon-btn size-8 hover:text-red-600"
                  @click="block(r)"
                >
                  <ShieldOff class="size-3.5" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePager
      v-model:page="page"
      :total="filtered.length"
      :per-page="perPage"
    />
  </section>
  <UiDrawer
    :open="modal"
    :title="form.id ? 'Kartı düzenle' : 'Yeni kart tanımla'"
    description="RFID kartı bir öğrenciyle eşleştirin ve erişim durumunu yönetin."
    size="md"
    @close="modal = false"
    ><form
      id="card-form"
      class="grid gap-5 sm:grid-cols-2"
      @submit.prevent="save"
    >
      <label v-if="session.isSuper" class="field sm:col-span-2"
        ><span class="field-label">Okul</span
        ><UiSelect
          v-model="form.schoolId"
          :options="schoolOptions"
          searchable
          search-placeholder="Okul ara..." /></label
      ><label class="field"
        ><span class="field-label">Kart UID</span
        ><input
          v-model="form.uid"
          class="input font-mono uppercase"
          required /></label
      ><label class="field"
        ><span class="field-label">Durum</span
        ><UiSelect v-model="form.status" :options="statusOptions" /></label
      ><label class="field sm:col-span-2"
        ><span class="field-label">Öğrenci</span
        ><UiSelect
          v-model="form.studentId"
          :options="studentOptions"
          searchable
          search-placeholder="Öğrenci adı veya numarası ara..." /></label
      ><label class="field sm:col-span-2"
        ><span class="field-label">Etiket</span
        ><input v-model="form.label" class="input"
      /></label>
    </form>
    <template #footer>
      <UiButton variant="secondary" @click="modal = false">Vazgeç</UiButton>
      <UiButton type="submit" form="card-form" :loading="saving"
        >Kartı Kaydet</UiButton
      >
    </template>
  </UiDrawer>
  <UiConfirmDrawer
    :open="Boolean(deleteTarget)"
    title="Kartı bloke et"
    :description="
      deleteTarget
        ? `${deleteTarget.uid} UID numaralı kart geçişlerde kullanılamayacak.`
        : ''
    "
    confirm-label="Kartı Bloke Et"
    :loading="deleting"
    @close="deleteTarget = null"
    @confirm="confirmBlock"
  />
  <UiToast :message="toast" :type="toastType" @close="toast = ''" />
</template>
